import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useMediaQuery } from "react-responsive";
import Cookies from "js-cookie";
import API_ENDPOINTS from "../config/api";

// 고유 ID 생성 함수
const encodeUUID = () => {
  const randomPart = Math.random().toString(36).substring(2, 10);
  const timestampPart = Date.now().toString(36);
  return `${randomPart}${timestampPart}`;
};

const PaymentBankTransferPage = () => {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery({ minWidth: 768 });
  const [isChecked, setIsChecked] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const lawsuitAmount = parseInt(Cookies.get("lawsuit_amount") || "5000", 10);

  // 입금 계좌 정보
  const bankInfo = {
    bankName: "신한은행",
    accountNumber: "110-123-456789",
    accountHolder: "법무법인 더에이치",
  };

  // 사용자 정보 가져오기
  useEffect(() => {
    const fetchUserData = async () => {
      setIsLoading(true);
      try {
        const token = Cookies.get(
          API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN
        );

        if (!token) {
          throw new Error("인증 토큰이 없습니다. 다시 로그인해주세요.");
        }

        const response = await fetch(`${API_ENDPOINTS.BASE_URL}/clients/me`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          throw new Error("사용자 정보를 가져오는데 실패했습니다.");
        }

        const data = await response.json();
        setUserData(data);
      } catch (err) {
        setError(err.message || "사용자 정보를 가져오는데 실패했습니다.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, []);

  // 뒤로 가기
  const handleBack = () => {
    navigate(-1);
  };

  // 계좌번호 복사 기능
  const handleCopyAccount = () => {
    if (bankInfo.accountNumber) {
      // 모바일 지원을 위한 fallback 복사 방법 추가
      if (navigator.clipboard) {
        navigator.clipboard
          .writeText(bankInfo.accountNumber)
          .then(() => {
            showCopySuccess();
          })
          .catch((err) => {
            fallbackCopy(bankInfo.accountNumber);
          });
      } else {
        fallbackCopy(bankInfo.accountNumber);
      }
    }
  };

  // 폴백 복사 메소드 (모바일 지원을 위해)
  const fallbackCopy = (text) => {
    // 임시 텍스트 영역 생성
    const textArea = document.createElement("textarea");
    textArea.value = text;

    // 화면 밖으로 위치시키기
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);

    // 선택 및 복사
    textArea.focus();
    textArea.select();

    let success = false;
    try {
      success = document.execCommand("copy");
    } catch (err) {}

    // 정리
    document.body.removeChild(textArea);

    // 성공 표시
    if (success) {
      showCopySuccess();
    }
  };

  // 복사 성공 표시
  const showCopySuccess = () => {
    setCopySuccess(true);
    setShowToast(true);

    // 2초 후 표시 제거
    setTimeout(() => {
      setCopySuccess(false);
      setShowToast(false);
    }, 2000);
  };

  // 애니메이션 변수
  const pageVariants = {
    initial: {
      opacity: 0,
    },
    in: {
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
    out: {
      opacity: 0,
      transition: {
        duration: 0.3,
      },
    },
  };

  const contentVariants = {
    initial: {
      opacity: 0,
      y: 20,
    },
    in: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 0.2,
        staggerChildren: 0.1,
      },
    },
  };

  // 토스트 애니메이션
  const toastVariants = {
    initial: { opacity: 0, y: 50 },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20,
      },
    },
    exit: {
      opacity: 0,
      y: 50,
      transition: {
        duration: 0.3,
      },
    },
  };

  // 입금 확인 요청 처리
  const handleSubmitRequest = () => {
    if (!isChecked) {
      return;
    }

    // 여기서는 실제 API 호출 없이 완료 페이지로 이동
    Object.keys(Cookies.get()).forEach((cookie) => {
      Cookies.remove(cookie);
    });
    navigate("/complete");
  };

  // 로딩 표시
  if (isLoading) {
    return (
      <PageBackground>
        <Container>
          <Header>
            <BackButton onClick={handleBack}>
              <BackIcon>&#10094;</BackIcon>
            </BackButton>
            <HeaderTitle>계좌이체</HeaderTitle>
          </Header>
          <Content>
            <LoadingContainer>
              <Spinner />
              <LoadingMessage>
                사용자 정보를 불러오는 중입니다...
              </LoadingMessage>
            </LoadingContainer>
          </Content>
        </Container>
      </PageBackground>
    );
  }

  // 에러 표시
  if (error) {
    return (
      <PageBackground>
        <Container>
          <Header>
            <BackButton onClick={handleBack}>
              <BackIcon>&#10094;</BackIcon>
            </BackButton>
            <HeaderTitle>계좌이체</HeaderTitle>
          </Header>
          <Content>
            <ErrorContainer>
              <ErrorIcon>⚠️</ErrorIcon>
              <ErrorTitle>오류가 발생했습니다</ErrorTitle>
              <ErrorDescription>{error}</ErrorDescription>
              <RetryButton onClick={() => window.location.reload()}>
                다시 시도하기
              </RetryButton>
            </ErrorContainer>
          </Content>
        </Container>
      </PageBackground>
    );
  }

  return (
    <PageBackground>
      <Container
        variants={pageVariants}
        initial="initial"
        animate="in"
        exit="out"
      >
        <Header>
          <BackButton onClick={handleBack}>
            <BackIcon>&#10094;</BackIcon>
          </BackButton>
          <HeaderTitle>계좌이체</HeaderTitle>
        </Header>
        <Content variants={contentVariants} initial="initial" animate="in">
          <ContentSection>
            <TitleWithIcon>
              <PayIcon src="/png/accountTransfer.png" alt="계좌이체 아이콘" />
              <SectionTitle>계좌이체 결제</SectionTitle>
            </TitleWithIcon>
            <SectionSubtitle>
              아래 계좌로 입금 후 입금 확인 요청을 해주세요
            </SectionSubtitle>

            {userData && (
              <UserInfoBox>
                <BoxTitle>결제자 정보</BoxTitle>
                <DetailRow>
                  <DetailLabel>이름</DetailLabel>
                  <DetailValue>{userData.name}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>연락처</DetailLabel>
                  <DetailValue>{userData.phoneNumber}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>이메일</DetailLabel>
                  <DetailValue>{userData.email}</DetailValue>
                </DetailRow>
              </UserInfoBox>
            )}

            <PaymentDetailsBox>
              <BoxTitle>결제 금액 상세</BoxTitle>
              <DetailRow>
                <DetailLabel>접수비</DetailLabel>
                <DetailValue>{lawsuitAmount.toLocaleString()}원</DetailValue>
              </DetailRow>
              <Divider />
              <TotalRow>
                <TotalLabel>총 결제 금액</TotalLabel>
                <TotalValue>{lawsuitAmount.toLocaleString()}원</TotalValue>
              </TotalRow>
            </PaymentDetailsBox>

            <BankAccountBox>
              <BoxTitle>입금 계좌 정보</BoxTitle>
              <BankInfoItem>
                <BankInfoLabel>은행명</BankInfoLabel>
                <BankInfoValue>{bankInfo.bankName}</BankInfoValue>
              </BankInfoItem>
              <BankInfoItem>
                <BankInfoLabel>계좌번호</BankInfoLabel>
                <BankInfoValueContainer>
                  <BankInfoValue>{bankInfo.accountNumber}</BankInfoValue>
                  <CopyButton onClick={handleCopyAccount}>
                    <CopyIcon />
                    {copySuccess ? "복사됨" : "복사"}
                  </CopyButton>
                </BankInfoValueContainer>
              </BankInfoItem>
              <BankInfoItem>
                <BankInfoLabel>예금주</BankInfoLabel>
                <BankInfoValue>{bankInfo.accountHolder}</BankInfoValue>
              </BankInfoItem>

              <InstructionText>
                • 위 계좌로 <strong>{lawsuitAmount.toLocaleString()}원</strong>
                을 입금해주세요.
              </InstructionText>
              <InstructionText>
                • 입금자명은 <strong>{userData?.name || "고객명"}</strong>(으)로
                해주세요.
              </InstructionText>
              <InstructionText>
                • 입금 후 아래 버튼을 클릭하면 신청이 완료됩니다.
              </InstructionText>
              <InstructionText>
                • 입금이 확인되면 담당자가 확인 후 처리해드립니다.
              </InstructionText>
            </BankAccountBox>

            <TermsSection>
              <TermsTitle>환불 규정 및 주의사항</TermsTitle>
              <TermsList>
                <TermsItem>
                  • 착수금 또는 관리비는 소송 진행을 위해 필요한 비용입니다.
                </TermsItem>
                <TermsItem>
                  • 납부 후 취소 시 접수비는 환불되지 않습니다.
                </TermsItem>
                <TermsItem>
                  • 소송 접수 후 취소 시 송달료는 실비 정산 후 환불됩니다.
                </TermsItem>
                <TermsItem>
                  • 소송 진행 중 항소 단계에서만 추가 비용이 발생할 수 있으며,
                  패소에 따른 별도 비용은 없습니다. 추가 비용이 생길 경우 사전에
                  안내해 드립니다.
                </TermsItem>
              </TermsList>

              <CheckboxContainer onClick={() => setIsChecked(!isChecked)}>
                <Checkbox checked={isChecked}>
                  {isChecked ? (
                    <CheckedImg src="/png/checkedYellow.png" alt="체크박스" />
                  ) : (
                    <CheckedImg src="/png/checkedinActive.png" alt="체크박스" />
                  )}
                </Checkbox>
                <CheckboxLabel>
                  위 내용을 모두 확인하였으며 동의합니다.
                </CheckboxLabel>
              </CheckboxContainer>
            </TermsSection>

            <ButtonContainer>
              <PaymentButton
                onClick={handleSubmitRequest}
                disabled={!isChecked}
              >
                입금했어요, 확인해주세요
              </PaymentButton>
            </ButtonContainer>
            <div style={{ height: "80px" }} />
          </ContentSection>
        </Content>
      </Container>

      {/* 토스트 알림 */}
      <AnimatePresence>
        {showToast && (
          <Toast
            variants={toastVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <CheckIcon>✓</CheckIcon>
            계좌번호가 클립보드에 복사되었습니다
          </Toast>
        )}
      </AnimatePresence>
    </PageBackground>
  );
};

// 스타일 컴포넌트
const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 0;
  background-color: #f8f8f8;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 769px) {
    padding: 40px 20px 60px;
    background-color: #fff;
  }
`;

const Container = styled(motion.div)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: #f8f8f8;

  /* 모바일 디바이스 */
  @media (max-width: 768px) {
    min-height: auto;
    box-shadow: none;
    padding: 0 0 80px 0;
    max-width: 100%;
    margin: 0;
    border-radius: 0;
  }

  /* 데스크탑 디바이스 */
  @media (min-width: 769px) {
    border-radius: 20px;
    min-height: auto;
    max-height: none;
    max-width: 470px;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
    padding: 20px;
  }
`;

const Header = styled.div`
  width: 100%;
  padding: 16px 0;
  text-align: center;
  position: relative;
  border-bottom: 1px solid #eee;
`;

const BackButton = styled.button`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BackIcon = styled.span`
  font-size: 20px;
  color: #333;
`;

const HeaderTitle = styled.h1`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 20px;
  box-sizing: border-box;
`;

const ContentSection = styled.div`
  width: 100%;
  max-width: 500px;
`;

const TitleWithIcon = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  margin-top: 10px;
`;

const PayIcon = styled.img`
  width: 32px;
  height: 32px;
  margin-right: 10px;
`;

const SectionTitle = styled.h2`
  font-size: 22px;
  font-weight: 700;
  color: #333;
  margin: 0;
`;

const SectionSubtitle = styled.p`
  font-size: 16px;
  color: #666;
  margin: 8px 0 24px 0;
`;

const UserInfoBox = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
`;

const BoxTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 20px 0;
  color: #333;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
`;

const DetailLabel = styled.span`
  color: #666;
`;

const DetailValue = styled.span`
  font-weight: 500;
  color: #333;
`;

const PaymentDetailsBox = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
`;

const BankAccountBox = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
`;

const BankInfoItem = styled.div`
  margin-bottom: 16px;
`;

const BankInfoLabel = styled.div`
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
`;

const BankInfoValue = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #333;
`;

const BankInfoValueContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CopyButton = styled.button`
  display: flex;
  padding: 8px 12px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 4px;
  background: #f2f9ff;
  border: none;
  cursor: pointer;
  color: #E31C20;
  font-size: 14px;
  font-weight: 500;
  -webkit-tap-highlight-color: transparent; /* 모바일 터치 하이라이트 제거 */
  touch-action: manipulation; /* 모바일 터치 최적화 */
  user-select: none; /* 텍스트 선택 방지 */

  &:active {
    background: #e0f0ff; /* 터치 시 피드백 */
    transform: scale(0.98); /* 약간의 눌림 효과 */
  }
`;

const CopyIcon = styled.span`
  display: inline-block;
  width: 16px;
  height: 16px;
  margin-right: 5px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%230075FF' viewBox='0 0 24 24'%3E%3Cpath d='M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z'/%3E%3C/svg%3E");
  background-size: contain;
  background-repeat: no-repeat;
`;

const InstructionText = styled.p`
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin: 8px 0;

  strong {
    color: #E31C20;
    font-weight: 600;
  }
`;

const Divider = styled.div`
  height: 1px;
  background-color: #eee;
  margin: 15px 0;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const TotalLabel = styled.span`
  font-weight: 600;
  color: #333;
`;

const TotalValue = styled.span`
  font-weight: 700;
  font-size: 18px;
  color: #333;
`;

const TermsSection = styled.div`
  margin-bottom: 24px;
`;

const TermsTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 15px 0;
  color: #333;
`;

const TermsList = styled.div`
  background-color: #f9f9f9;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
`;

const TermsItem = styled.p`
  font-size: 14px;
  color: #555;
  margin: 8px 0;
  line-height: 1.5;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 8px 0;
`;

const Checkbox = styled.div`
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const CheckedImg = styled.img`
  max-width: 100%;
  max-height: 100%;
`;

const CheckboxLabel = styled.span`
  font-size: 14px;
  margin-left: 10px;
  color: #333;
  font-weight: 500;
`;

const ButtonContainer = styled.div`
  width: 100%;
  margin-top: 20px;
`;

const PaymentButton = styled.button`
  display: flex;
  width: 100%;
  height: 56px;
  padding: 0px 14px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border-radius: 8px;
  background: ${(props) => (props.disabled ? "#ccc" : "#E31C20")};
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  border: none;
  opacity: ${(props) => (props.disabled ? 0.7 : 1)};
  transition: background-color 0.2s;
`;

// 로딩 관련 컴포넌트
const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  width: 100%;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 117, 255, 0.1);
  border-radius: 50%;
  border-left-color: #E31C20;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingMessage = styled.p`
  font-size: 16px;
  color: #666;
  text-align: center;
`;

// 에러 관련 컴포넌트
const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  width: 100%;
  text-align: center;
`;

const ErrorIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

const ErrorTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0 0 8px 0;
`;

const ErrorDescription = styled.p`
  font-size: 16px;
  color: #666;
  margin: 0 0 24px 0;
`;

const RetryButton = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  background-color: #E31C20;
  color: white;
  font-size: 16px;
  font-weight: 600;
  border: none;
  cursor: pointer;
`;

// 토스트 알림 스타일
const Toast = styled(motion.div)`
  position: fixed;
  bottom: 30px;
  left: 50%;
  /* 데스크탑에서는 32%로 위치 조정 */
  @media (min-width: 768px) {
    max-width: 400px;
    min-width: 40%;
    left: 28.5%;
  }

  transform: translateX(-50%);
  background-color: rgba(46, 56, 64, 0.9);
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-size: 14px;
  z-index: 1000;
  min-width: 200px;
  max-width: 90%;
`;

const CheckIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background-color: #E31C20;
  color: white;
  border-radius: 50%;
  margin-right: 8px;
  font-size: 16px;
  font-weight: bold;
`;

export default PaymentBankTransferPage;
